import React, { useEffect, useMemo, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { SectionHeader } from '../shared';
import { TenantSubscriptionExtend } from '../TenantSubscriptionExtend';
import { TenantUsagePanel, TenantUsageSummaryRow, UsageMiniStats } from '../TenantUsagePanel';
import type { SuperAdminContext } from '../types';

export function UsageSection({ ctx }: { ctx: SuperAdminContext }) {
  const { activeTenantSummaries, dateLabel, selectedOrgUsage, loadOrgUsage, usageLoadingFor, activeOrgs, reload } = ctx;
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeTenantSummaries;
    return activeTenantSummaries.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.adminEmail.toLowerCase().includes(q) ||
        (t.planId || '').toLowerCase().includes(q) ||
        t.organizationId.toLowerCase().includes(q)
    );
  }, [activeTenantSummaries, query]);

  const findOrg = (orgId: string) => activeOrgs.find((o) => o.id === orgId);

  useEffect(() => {
    if (!selectedOrgUsage) return;
    const id = window.requestAnimationFrame(() => {
      document.getElementById('tenant-usage-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedOrgUsage]);

  const refreshTenantDetail = async () => {
    if (!selectedOrgUsage) return;
    await reload();
    const org = activeOrgs.find((o) => o.id === selectedOrgUsage.organization.id) ?? {
      ...selectedOrgUsage.organization,
      dataKeys: undefined,
      whatsappMessagesInPeriod: undefined,
    };
    await loadOrgUsage(org);
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Usage & storage"
        subtitle="Active subscribers only — media, MongoDB, records, and SMS. SMS cost is per messages sent to that shop’s customers in the date range (not customer headcount). Pending checkouts are excluded."
      />

      <UsageMiniStats tenants={activeTenantSummaries} />

      <div className="relative max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tenant, email, plan…"
          className="h-10 w-full rounded-lg border border-zinc-200 bg-white py-0 pl-9 pr-3 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/15"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-md">
        <div className="border-b border-zinc-100 bg-zinc-50/90 px-4 py-3">
          <p className="text-sm font-semibold text-zinc-900">Active tenants — usage snapshot</p>
          <p className="text-xs text-zinc-500">{dateLabel} · {filtered.length} rows</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] text-left text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-100/80 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-3 py-3">Tenant</th>
                <th className="px-3 py-3">Plan</th>
                <th className="px-3 py-3">Billing</th>
                <th className="px-3 py-3">Plan ends</th>
                <th className="px-3 py-3">Media storage</th>
                <th className="px-3 py-3">Database storage</th>
                <th className="px-3 py-3 text-right">Records</th>
                <th className="px-3 py-3 text-right">SMS sent</th>
                <th className="px-3 py-3 text-right">SMS cost</th>
                <th className="px-3 py-3 text-right">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-sm text-zinc-500">
                    No tenants match your search.
                  </td>
                </tr>
              ) : (
                filtered.map((t) => {
                  const org = findOrg(t.organizationId);
                  return (
                    <TenantUsageSummaryRow
                      key={t.organizationId}
                      t={t}
                      onDetail={org ? () => void loadOrgUsage(org) : undefined}
                      loading={usageLoadingFor === t.organizationId}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedOrgUsage ? (
        <div id="tenant-usage-detail" className="scroll-mt-6 rounded-xl border border-emerald-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Tenant drill-down</p>
              <h3 className="mt-1 text-lg font-semibold text-zinc-900">{selectedOrgUsage.organization.name}</h3>
              <p className="text-xs text-zinc-500">{selectedOrgUsage.organization.adminEmail}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void ctx.onDeleteOrg(selectedOrgUsage.organization)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete tenant
              </button>
              <button
                type="button"
                onClick={ctx.clearOrgUsage}
                className="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
              >
                Close
              </button>
            </div>
          </div>
          <div className="mt-4 space-y-4">
            <TenantSubscriptionExtend detail={selectedOrgUsage} onUpdated={refreshTenantDetail} />
            <TenantUsagePanel detail={selectedOrgUsage} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
